import _extends from "@babel/runtime/helpers/extends";
import React, { useCallback } from 'react';
import { useIntl } from 'react-intl-next';
import { JiraIcon, ConfluenceIcon } from '@atlaskit/logo';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss, Text } from '@atlaskit/primitives';
import { useQuery } from '../query-context';
import { AdvancedSearchFooterLink, ReturnIconWithModifier } from './advanced-search-footer';
import { messages } from '../messages';
import { getDefaultAtlasBaseURL } from '../products/townsquare/url-generators';
import { GoogleDriveIcon } from './icons/google-icon';
import { SlackIcon } from './icons/slack-icon';
import { getTrigger, onAdvancedSearchSelected, useAnalytics } from '../../common/analytics';
import { useProductContext } from '../product-router';
import { AdvancedSearchLink } from '../../common/advanced-search-link';
var ModifierIcon = function ModifierIcon() {
  return /*#__PURE__*/React.createElement(ReturnIconWithModifier, {
    showModifierKey: true
  });
};
var ReturnIcon = function ReturnIcon() {
  return /*#__PURE__*/React.createElement(ReturnIconWithModifier, null);
};
var containerStyles = xcss({
  display: 'flex'
});
var productIconStyles = xcss({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  width: '78px'
});
var moreButtonStyles = xcss({
  backgroundColor: 'color.background.neutral',
  borderRadius: 'border.radius.100',
  paddingInline: 'space.075',
  marginRight: 'space.050',
  height: '20px',
  display: 'flex',
  alignItems: 'center'
});
var absoluteStyles = xcss({
  position: 'absolute'
});
var borderStyles = xcss({
  backgroundColor: 'elevation.surface.overlay',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '22px',
  width: '22px',
  borderRadius: '6px'
});
var jiraIconOverlapStyles = xcss({
  zIndex: 'dialog',
  transform: 'translate(15px, 0)'
});
var confluenceIconOverlapStyles = xcss({
  zIndex: 'navigation',
  transform: 'translate(30px, 0)'
});
var googleDriveIconOverlapStyles = xcss({
  zIndex: 'card',
  transform: 'translate(45px, 0)'
});
var slackIconOverlapStyles = xcss({
  transform: 'translate(60px, 0)'
});
var IconComponent = function IconComponent(_ref) {
  var showModifierIcon = _ref.showModifierIcon;
  var _useIntl = useIntl(),
    formatMessage = _useIntl.formatMessage;
  return /*#__PURE__*/React.createElement(Box, {
    xcss: containerStyles
  }, /*#__PURE__*/React.createElement(Box, {
    xcss: productIconStyles
  }, /*#__PURE__*/React.createElement(Box, {
    xcss: [absoluteStyles, jiraIconOverlapStyles, borderStyles]
  }, /*#__PURE__*/React.createElement(JiraIcon, {
    size: "xsmall",
    shouldUseNewLogoDesign: true
  })), /*#__PURE__*/React.createElement(Box, {
    xcss: [absoluteStyles, confluenceIconOverlapStyles, borderStyles]
  }, /*#__PURE__*/React.createElement(ConfluenceIcon, {
    size: "xsmall",
    shouldUseNewLogoDesign: true
  })), /*#__PURE__*/React.createElement(Box, {
    xcss: [absoluteStyles, googleDriveIconOverlapStyles]
  }, /*#__PURE__*/React.createElement(GoogleDriveIcon, null)), /*#__PURE__*/React.createElement(Box, {
    xcss: [absoluteStyles, slackIconOverlapStyles]
  }, /*#__PURE__*/React.createElement(SlackIcon, null))), /*#__PURE__*/React.createElement(Box, {
    xcss: moreButtonStyles
  }, /*#__PURE__*/React.createElement(Text, {
    size: "small",
    align: "center",
    color: "color.text.subtle"
  }, formatMessage(messages.more))), showModifierIcon ? /*#__PURE__*/React.createElement(ModifierIcon, null) : /*#__PURE__*/React.createElement(ReturnIcon, null));
};
export var getAtlassianHomeSearchUrl = function getAtlassianHomeSearchUrl(orgId, cloudId, query, primaryProduct) {
  var url = new URL("".concat(getDefaultAtlasBaseURL(true), "/o/").concat(orgId, "/search"));
  url.searchParams.set('cloudId', cloudId);
  if (primaryProduct) {
    url.searchParams.set('referrer', primaryProduct);
  }
  if (query) {
    url.searchParams.set('text', query);
  }
  return url.toString();
};
export var AtlassianHomeSearchLink = function AtlassianHomeSearchLink(_ref2) {
  var orgId = _ref2.orgId,
    cloudId = _ref2.cloudId,
    showModifierIcon = _ref2.showModifierIcon,
    actionSubjectId = _ref2.actionSubjectId;
  var _useIntl2 = useIntl(),
    formatMessage = _useIntl2.formatMessage;
  var _useQuery = useQuery(),
    query = _useQuery.query,
    isLoading = _useQuery.isLoading;
  var _useAnalytics = useAnalytics(),
    fireAnalyticsEvent = _useAnalytics.fireAnalyticsEvent;
  var _useProductContext = useProductContext(),
    getActiveProduct = _useProductContext.getActiveProduct,
    getPrimaryProduct = _useProductContext.getPrimaryProduct;
  var primaryProduct = getPrimaryProduct();
  var activeProduct = getActiveProduct();
  var href = getAtlassianHomeSearchUrl(orgId, cloudId, query, primaryProduct === null || primaryProduct === void 0 ? void 0 : primaryProduct.id);
  var productName = activeProduct === null || activeProduct === void 0 ? void 0 : activeProduct.title;
  var defaultActionSubjectId = "".concat(productName, "AdvancedSearchLink");
  var onAdvancedSearchClicked = useCallback(function (e) {
    fireAnalyticsEvent(onAdvancedSearchSelected({
      trigger: getTrigger(e),
      actionSubjectId: actionSubjectId || defaultActionSubjectId,
      isLoading: isLoading,
      newTab: true,
      isRovoCrossProductSearchLink: true
    }));
  }, [fireAnalyticsEvent, isLoading, actionSubjectId, defaultActionSubjectId]);
  return /*#__PURE__*/React.createElement(AdvancedSearchFooterLink, {
    href: href,
    onClick: onAdvancedSearchClicked,
    linkComponent: function linkComponent(props) {
      return /*#__PURE__*/React.createElement(AdvancedSearchLink, _extends({}, props, {
        isKeyboardHighlighted: !!props.isKeyboardHighlighted,
        href: href,
        target: "_blank"
      }));
    },
    contentLabel: formatMessage(messages.extensible_advanced_search_cross_product_footer),
    returnKeyIconComponent: function returnKeyIconComponent() {
      return /*#__PURE__*/React.createElement(IconComponent, {
        showModifierIcon: showModifierIcon
      });
    }
  });
};